import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { object, string, type ObjectSchema } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js'

import { SystemUserRoles, type SystemUserRolesType } from '@/api/types/Organization'

// TODO: Replace this with UserResponse type
interface AuthUser {
  id: number | string;
  name: string;
  surname?: string;
  email: string;
  location: string;
  role: SystemUserRolesType;
  company: string;
  avatar?: string;
}

type AuthUserFormType = Omit<AuthUser, 'id'>

const useAuthUserForm = (user: Partial<AuthUser>) => {
  const defaultValues = useCallback((u: Partial<AuthUser>): AuthUserFormType => ({
    ...u,
    name: u.name || '',
    surname: u.surname || '',
    email: u.email || '',
    location: u.location || '',
    company: u.company || '',
    role: u.role || 'DELEGATE',
    avatar: u.avatar || '',
  }), [])

  const validationSchema = useCallback((): ObjectSchema<AuthUserFormType> => (
    object({
      name: string().min(2).max(100).required(),
      surname: string().min(2).max(100).required(),
      email: string().email().required(),
      location: string().min(2).max(100).required(),
      company: string().min(2).max(100).required(),
      role: string()
        .oneOf([...SystemUserRoles], 'Invalid role')
        .required('Role is required'),
      avatar: string().optional(),
    })
  ), [])

  const form = useForm<AuthUserFormType>({
    defaultValues: defaultValues(user),
    resolver: yupResolver(validationSchema()),
  })

  const resetForm = useCallback((u: Partial<AuthUser>) => {
    form.reset(defaultValues(u))
  }, [form, defaultValues])

  return {
    form,
    resetForm,
  }
}

export default useAuthUserForm
